import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { getToolName } from 'ai'
import type { DynamicToolUIPart, ToolUIPart } from 'ai'
import { decodeApprovalCard, isAssistantToolError } from '../contract'
import AssistantApprovalCard, {
  type AssistantApprovalPhase,
} from './AssistantApprovalCard'

type AnyToolPart = ToolUIPart | DynamicToolUIPart

export interface AssistantApprovalDecision {
  id: string
  approved: boolean
  /** Which change this was, for usage counts only. */
  action: string
}

/** True when this part carries an approval the user can see or answer. */
export const hasApprovalCard = (part: AnyToolPart) =>
  part.state === 'approval-requested' ||
  Boolean(part.approval?.requestReason && !part.approval.isAutomatic)

const phaseOf = (part: AnyToolPart): AssistantApprovalPhase => {
  switch (part.state) {
    case 'approval-requested':
      return 'pending'
    case 'approval-responded':
      return part.approval.approved ? 'approved' : 'cancelled'
    case 'output-denied':
      return 'cancelled'
    case 'output-error':
      return 'failed'
    case 'output-available':
      return isAssistantToolError(part.output) ? 'failed' : 'done'
    default:
      return 'approved'
  }
}

interface AssistantApprovalPartProps {
  part: AnyToolPart
  onDecide?: (decision: AssistantApprovalDecision) => void
  disabled?: boolean
}

/**
 * Turns a change waiting for (or past) the user's OK into its card. Only the
 * server-written card in `requestReason` is shown; if it can't be read the
 * change can only be cancelled.
 */
const AssistantApprovalPart = ({
  part,
  onDecide,
  disabled,
}: AssistantApprovalPartProps) => {
  const approvalId = part.approval?.id
  const card = decodeApprovalCard(part.approval?.requestReason)
  const phase = phaseOf(part)
  const decide = (approved: boolean) => {
    if (approvalId) {
      onDecide?.({ id: approvalId, approved, action: getToolName(part) })
    }
  }
  const canDecide = Boolean(onDecide && approvalId) && !disabled

  if (!card) {
    if (phase !== 'pending') return null
    return (
      <Box
        data-testid="assistant-approval-unreadable"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          I couldn’t load the details for this change, so it can’t be approved.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => decide(false)}
          disabled={!canDecide}
        >
          Cancel
        </Button>
      </Box>
    )
  }

  return (
    <AssistantApprovalCard
      card={card}
      phase={phase}
      onApprove={() => decide(true)}
      onCancel={() => decide(false)}
      disabled={!canDecide}
    />
  )
}

export default AssistantApprovalPart
